import { IconTrash } from 'assets/img';
import AddIcon from '@mui/icons-material/Add';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { LwButton, LwFormTime } from 'redesign';
import { DateTime } from 'luxon';
import { useEffect, useMemo } from 'react';
import { Grid, styled } from '@mui/material';
import { SegmentsForEdit, TimeSlotFormContext } from './time-slot.types';
import { isOverlappingSegment } from '../utils/is-overlapping-segment';

type TimeslotProps = {
  index: number;
  handleDelete: (index: number) => void;
  handleAdd: (index: number) => void;
  canAdd: boolean;
  isWorkerFilled: boolean;
  additionalAvailabilities?: SegmentsForEdit[];
};
export const Timeslot = ({
  index,
  handleDelete,
  handleAdd,
  canAdd,
  isWorkerFilled = true,
  additionalAvailabilities,
}: TimeslotProps) => {
  const { t } = useTranslation('all-platforms-common');
  const { control, watch, setError, clearErrors } = useFormContext<TimeSlotFormContext>();
  const timeslots = watch('availability');

  //I don't like this at all, but there's something extremely off about how array values play with the native validation
  const validateTimeSlot = (slot: SegmentsForEdit) => {
    const overlappingSlot = isOverlappingSegment(slot, [
      ...timeslots.filter((_, i) => i !== index),
      ...(additionalAvailabilities ?? []),
    ]);
    const isInternallyInvalid =
      DateTime.fromISO(slot.utcStartDateTime) >= DateTime.fromISO(slot.utcEndDateTime);

    const startField = `availability.${index}.utcStartDateTime` as const;
    const endField = `availability.${index}.utcEndDateTime` as const;
    const overlappingErrorMessage = t('edit_availability.timeslot_overlap', {
      ns: 'all-platforms-common',
    });
    const internallyInvalidErrorMessage = t('edit_availability.timeslot_invalid', {
      ns: 'all-platforms-common',
    });
    if (isInternallyInvalid) {
      const error = { type: 'manual', message: internallyInvalidErrorMessage };
      setError(startField, error);
      setError(endField, error);
    } else if (overlappingSlot) {
      const error = { type: 'manual', message: overlappingErrorMessage };
      setError(startField, error);
      setError(endField, error);
    } else {
      clearErrors([startField, endField]);
    }
  };

  const isDeleteButtonDisabled = useMemo(() => {
    return timeslots.length <= 1;
  }, [timeslots.length]);
  const isAddButtonDisabled = useMemo(() => {
    const luxonStartTime = DateTime.fromISO(timeslots[index].utcStartDateTime);
    const luxonEndTime = DateTime.fromISO(timeslots[index].utcEndDateTime);
    return (
      !isWorkerFilled ||
      !canAdd ||
      !luxonStartTime.isValid ||
      !luxonEndTime.isValid ||
      luxonStartTime >= luxonEndTime
    );
  }, [isWorkerFilled, canAdd, timeslots[index].utcStartDateTime, timeslots[index].utcEndDateTime]);

  useEffect(() => {
    validateTimeSlot(timeslots[index]);
    //This dependency needs to be stringified like this because the logic contained within validateTimeSlot
    //relies on the properties of every member of the timeslots array
    //forgive me
  }, [JSON.stringify(timeslots), JSON.stringify(additionalAvailabilities)]);

  return (
    <StyledContainer container xs={24}>
      <StyledGrid container item xs={12} columnSpacing={0.1} justifyContent={'flex-start'}>
        <Grid item xs={5}>
          <LwButton
            startIcon={<IconTrash />}
            disabled={isDeleteButtonDisabled}
            color="secondary"
            onClick={() => handleDelete(index)}
          >
            {t('edit_availability.delete_timeslot', { ns: 'all-platforms-common' })}
          </LwButton>
        </Grid>
        {canAdd ? (
          <Grid item xs={3}>
            <LwButton
              startIcon={<AddIcon />}
              color="primary"
              onClick={() => handleAdd(index)}
              disabled={isAddButtonDisabled}
            >
              {t('edit_availability.add_timeslot', { ns: 'all-platforms-common' })}
            </LwButton>
          </Grid>
        ) : null}
      </StyledGrid>
      <Grid container spacing={2} xs={12}>
        <Grid item xs={6}>
          <LwFormTime
            name={`availability.${index}.utcStartDateTime`}
            label={t('edit_availability.start_time', { ns: 'all-platforms-common' })}
            control={control}
            rules={{ required: true }}
          />
        </Grid>
        <Grid item xs={6}>
          <LwFormTime
            name={`availability.${index}.utcEndDateTime`}
            label={t('edit_availability.end_time', { ns: 'all-platforms-common' })}
            control={control}
            rules={{ required: true }}
          />
        </Grid>
      </Grid>
    </StyledContainer>
  );
};

const StyledContainer = styled(Grid)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2),
}));

const StyledGrid = styled(Grid)(({ theme }) => ({
  marginTop: theme.spacing(4),
  gap: theme.spacing(1),
  flexGrow: 1,
}));
